// src/components/SlideSheet.jsx
//
// Shared animated drawer wrapper. Slides a panel in from the right with
// a concurrent scrim fade. Respects prefers-reduced-motion (fade only, no transform).
//
// Props:
//   open          boolean   — controls visibility
//   onClose       function  — called on scrim click or Escape key
//   width         number    — panel width in px (default 420)
//   children      node      — panel content
//   scrimTestId   string    — optional data-testid forwarded to the scrim div
//   panelTestId   string    — optional data-testid forwarded to the panel div

import { useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { DrawerCloseButton } from "../v2/components/DrawerCloseButton.jsx";

const EASE = [0.4, 0, 0.2, 1];
const DURATION = 0.25;

export function SlideSheet({ open, onClose, width = 420, children, scrimTestId, panelTestId, closeTestId, hideCloseButton = false, layer = 1100 }) {
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const panelVariants = reduceMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : { initial: { x: "100%" }, animate: { x: 0 }, exit: { x: "100%" } };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="slide-sheet-scrim"
            data-testid={scrimTestId}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: DURATION, ease: EASE }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(16,36,58,.28)",
              zIndex: layer,
            }}
          />
          <motion.div
            key="slide-sheet-panel"
            data-testid={panelTestId}
            data-feedback-obscuring-overlay="true"
            initial={panelVariants.initial}
            animate={panelVariants.animate}
            exit={panelVariants.exit}
            transition={{ duration: DURATION, ease: EASE }}
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              bottom: 0,
              width,
              background: "var(--v2-card)",
              boxShadow: "-8px 0 32px rgba(16,36,58,.18)",
              overflowY: "auto",
              zIndex: layer + 1,
            }}
          >
            {!hideCloseButton && (
              <DrawerCloseButton onClick={onClose} testId={closeTestId} />
            )}
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
